import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Assignments API service
 */
// Priority mapping: backend now requires integer values instead of strings
// 0:none, 1:low, 2:medium, 3:high, 4:critical
const PRIORITY_MAP = {
  none: 0,
  low: 1,
  medium: 2,
  med: 2, // tolerance
  normal: 2, // tolerance
  high: 3,
  critical: 4,
  urgent: 4 // tolerance
};

function normalizePriority(value) {
  if (value === null || value === undefined) return undefined; // let backend default if omitted
  if (typeof value === 'number' && value >= 0 && value <= 4) return value; // already numeric
  if (typeof value === 'string') {
    const key = value.trim().toLowerCase();
    if (PRIORITY_MAP.hasOwnProperty(key)) return PRIORITY_MAP[key];
    // handle ENUM-style strings like 'HIGH', 'MEDIUM'
    if (PRIORITY_MAP.hasOwnProperty(key.toLowerCase())) return PRIORITY_MAP[key.toLowerCase()];
  }
  // Fallback to medium (2) to avoid backend 400 on unexpected string (previous default behavior)
  return 2;
}
export const AssignmentApi = {
  /**
   * Get all assignments
   * @returns {Promise<Response>} All assignments response
   */
  getAssignments: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments`, {
      method: 'GET',
    });
  },

  /**
   * Get assignment by ID
   * @param {string|number} id - Assignment ID
   * @returns {Promise<Response>} Assignment response
   */
  getAssignment: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments/${id}`, {
      method: 'GET',
    });
  },

  /**
   * Create assignment
   * @param {Object} data - Assignment data
   * @returns {Promise<Response>} Create response
   */
  createAssignment: async (data) => {
    const payload = { ...data };
    if (payload.hasOwnProperty('priority')) {
      payload.priority = normalizePriority(payload.priority);
    }
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  /**
   * Update assignment
   * @param {string|number} id - Assignment ID
   * @param {Object} data - Updated assignment data
   * @returns {Promise<Object|null>} Updated assignment object, or null for 204 No Content
   * @throws {Error} On network errors or HTTP errors
   */
  updateAssignment: async (id, data) => {
    const payload = { ...data };
    if (payload.hasOwnProperty('priority')) {
      payload.priority = normalizePriority(payload.priority);
    }
    
    console.log('[AssignmentApi] updateAssignment request:', {
      id,
      url: `${DATA_SERVICE_BASE_URL}/api/assignments/${id}`,
      method: 'PUT',
      payloadCompleted: payload.completed,
      fullPayload: JSON.stringify(payload, null, 2)
    });
    
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    
    console.log('[AssignmentApi] updateAssignment response:', {
      id,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    // Handle 204 No Content (success with empty body)
    if (response.status === 204) {
      return null;
    }

    // Throw on HTTP errors
    if (!response.ok) {
      let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorBody = await response.json();
        errorMsg = errorBody.message || errorBody.error || errorMsg;
      } catch (e) {
        // Failed to parse error body, use default message
      }
      throw new Error(errorMsg);
    }

    // Parse response body
    const text = await response.text();
    if (!text) {
      // Empty body but 2xx status - treat as success with null
      return null;
    }

    try {
      const parsed = JSON.parse(text);
      console.log('[AssignmentApi] updateAssignment parsed response:', {
        id,
        completedField: parsed.completed,
        fullResponse: JSON.stringify(parsed, null, 2)
      });
      return parsed;
    } catch (parseError) {
      console.warn('Failed to parse assignment update response:', parseError);
      throw new Error('Invalid JSON response from server');
    }
  },

  /**
   * Mark assignment as completed
   */
  markAssignmentCompleted: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments/${id}/complete`, {
      method: 'PATCH',
    });
  },

  /**
   * Bulk-mark multiple assignments as completed in a single request
   * @param {Array<number|string>} ids - Assignment IDs to complete
   * @returns {Promise<Response>} Response with { updated: number }
   */
  bulkMarkAssignmentsCompleted: async (ids) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments/bulk-complete`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ids),
    });
  },

  // NOTE: No dedicated "incomplete" endpoint exists. Use updateAssignment instead.
  /**
   * Delete assignment
   * @param {string|number} id - Assignment ID
   * @returns {Promise<Response>} Delete response
   */
  deleteAssignment: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get assignments by endeavor
   * @param {string|number} endeavorId - Endeavor ID
   * @returns {Promise<Response>} Endeavor assignments response
   */
  getAssignmentsByEndeavor: async (endeavorId) => {
    // Updated to use aggregated user assignments endpoint with endeavor filter
    const params = new URLSearchParams({
      endeavorId: String(endeavorId),
      completed: 'false',
      page: '0',
      size: '10'
    }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/assignments?${params}`, {
      method: 'GET'
    });
  },

  /**
   * Get assignments by event
   * @param {string|number} eventId - Event ID
   * @returns {Promise<Response>} Event assignments response
   */
  getAssignmentsByEvent: async (eventId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/events/${eventId}/assignments`, {
      method: 'GET',
    });
  },

  /**
   * Get assignments by due date range
   * @param {string} start - Start date (ISO string)
   * @param {string} end - End date (ISO string)
   * @returns {Promise<Response>} Assignments in date range response
   */
  getAssignmentsByDueDateRange: async (start, end) => {
    const queryParams = new URLSearchParams({ start, end }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments/due-date-range?${queryParams}`, {
      method: 'GET',
    });
  },

  /**
   * Get overdue assignments
   * @param {string} [dateTime] - Optional custom date to check against
   * @returns {Promise<Response>} Overdue assignments response
   */
  getOverdueAssignments: async (dateTime = null) => {
    const queryParams = dateTime ? new URLSearchParams({ dateTime }).toString() : '';
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments/overdue${queryParams ? '?' + queryParams : ''}`, {
      method: 'GET',
    });
  },

  /**
   * Get completed assignments
   * @returns {Promise<Response>} Completed assignments response
   */
  getCompletedAssignments: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments/completed`, {
      method: 'GET',
    });
  },

  /**
   * Get incomplete assignments
   * @returns {Promise<Response>} Incomplete assignments response
   */
  getIncompleteAssignments: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/assignments/incomplete`, {
      method: 'GET',
    });
  },

  /**
   * Get filtered user assignments
   * @param {Object} options - Filter options
   * @returns {Promise<Response>} Filtered user assignments response
   */
  getUserAssignmentsFiltered: async (options = {}) => {
    const queryParams = new URLSearchParams(options).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/assignments${queryParams ? '?' + queryParams : ''}`, {
      method: 'GET',
    });
  }
};